import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { usePwaInstall } from "../contexts/PwaInstallContext";
import { Menu, LogOut, X } from "lucide-react";
import MorphingGrid from "./MorphingGrid";
import IosInstallModal from "./IosInstallModal";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { canInstall, promptInstall, isIOSSafari, isIOSNonSafari, isMobileDevice, installLabel } = usePwaInstall();
  const [menuOpen, setMenuOpen] = useState(false);
  const [iosModalOpen, setIosModalOpen] = useState(false);

  if (!user) return null;

  // Bottone X di chiusura accanto al burger: visibile su QUALSIASI
  // pagina di piano-cura (sia readonly via "occhio" sia editable via
  // "pennino") quando l'utente ci è arrivato dall'Archivio o da
  // "Chiama Paziente" (flag `archiveReturnActive` / `chiamaReturnActive`
  // settati nei rispettivi `handleEdit`). Permette di chiudere e tornare
  // alla pagina di origine.
  const returnPath = (() => {
    if (!/^\/piano-cura-/.test(location.pathname)) return null;
    try {
      const promemoriaKind = sessionStorage.getItem("promemoriaReturnActive");
      if (promemoriaKind === "clinico") return "/promemoria-clinico";
      if (promemoriaKind === "orto") return "/promemoria-ortodonzia";
      if (sessionStorage.getItem("chiamaReturnActive") === "1") return "/chiama-paziente";
      if (sessionStorage.getItem("archiveReturnActive") === "1") return "/opportunita-create";
      if (sessionStorage.getItem("pianiCuraReturnActive") === "1") return "/piani-cura";
    } catch { /* ignore */ }
    return null;
  })();
  const isFromArchive = !!returnPath;

  const handleCloseReadonly = () => {
    try {
      sessionStorage.removeItem("archiveReturnActive");
      sessionStorage.removeItem("chiamaReturnActive");
      sessionStorage.removeItem("promemoriaReturnActive");
      sessionStorage.removeItem("pianiCuraReturnActive");
    } catch { /* ignore */ }
    navigate(returnPath || "/opportunita-create");
  };

  // Admin has full access; non-admins see a page only if it's in `allowed_pages`.
  const isAdmin = user.role === "admin";
  const allowed = user.allowed_pages || [];
  const can = (page) => isAdmin || allowed.includes(page);

  // Handler unico per il bottone "Installa App" del menu:
  // - iOS Safari → apre il modal con le istruzioni Condividi → Aggiungi a Home
  //   (Apple non espone il prompt nativo).
  // - iOS Chrome/Firefox/Edge → apre il modal "Apri in Safari" (su iPhone
  //   solo Safari può installare PWA per limitazione Apple).
  // - Altri (Chrome desktop/Android) → invoca il prompt nativo del browser.
  // In tutti i casi chiude prima il fullscreen menu così l'utente vede
  // il modal/prompt centrato.
  const handleInstallClick = () => {
    setMenuOpen(false);
    if (isIOSSafari || isIOSNonSafari) {
      // Piccolo delay per far chiudere l'animazione del menu prima di
      // mostrare il modal (evita flash sovrapposto).
      setTimeout(() => setIosModalOpen(true), 80);
    } else {
      promptInstall();
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200/80 shadow-sm">
        <div className="w-full px-4 sm:px-6 lg:px-[100px] h-14 flex items-center justify-between">
          {/* Left: hamburger (+ X di chiusura readonly se attiva) */}
          <div className="flex items-center gap-1">
            <button onClick={() => setMenuOpen(true)} className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 transition-colors" data-testid="hamburger-menu">
              <Menu className="w-5 h-5" />
            </button>
            {isFromArchive && (
              <button
                onClick={handleCloseReadonly}
                className="w-10 h-10 flex items-center justify-center rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors border border-red-200"
                title={returnPath === "/chiama-paziente" ? "Chiudi e torna a Chiama Paziente" : returnPath === "/piani-cura" ? "Chiudi e torna alla Pipeline" : returnPath?.startsWith("/promemoria-") ? "Chiudi e torna ai Promemoria" : "Chiudi e torna all'Archivio Piani di Cura"}
                data-testid="close-readonly-btn"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Center: welcome */}
          <div className="flex items-center gap-2" data-testid="navbar-welcome">
            <span className="text-sm font-medium text-slate-700">
              Benvenuto, <span className="font-semibold text-[#3B42DD]">{user.name || user.email}</span>
            </span>
          </div>

          {/* Right: logout */}
          <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors" data-testid="logout-btn">
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Esci</span>
          </button>
        </div>
      </nav>

      {/* Fullscreen Menu */}
      {menuOpen && (
        <MorphingGrid
          className="fixed inset-0 z-50 flex flex-col menu-blueprint-bg overflow-hidden"
          data-testid="fullscreen-menu"
        >
          <div className="flex items-center justify-between px-6 sm:px-20 pt-6 sm:pt-8">
            <img
              src="/bdd-logo-white.svg"
              alt="BDD Studio Dentistico"
              className="h-10 sm:h-14 w-auto"
              data-testid="menu-logo"
            />
            <button onClick={() => setMenuOpen(false)} className="w-12 h-12 flex items-center justify-center rounded-xl text-white hover:bg-white/10 transition-colors" data-testid="close-fullscreen-menu">
              <X className="w-6 h-6" />
            </button>
          </div>
          <MenuBody
            isAdmin={isAdmin}
            user={user}
            can={can}
            canInstall={canInstall}
            isMobileDevice={isMobileDevice}
            installLabel={installLabel}
            onInstallClick={handleInstallClick}
            onNavigate={(path) => { setMenuOpen(false); navigate(path); }}
          />
        </MorphingGrid>
      )}
      <IosInstallModal
        open={iosModalOpen}
        onClose={() => setIosModalOpen(false)}
        variant={isIOSNonSafari ? "open-in-safari" : "safari"}
      />
    </>
  );
}

// Voce del menu: stile uniforme + testid. Estratta per evitare ripetizione.
// `variant`:
//   - "default": testo grande bianco (colonna principale e top-list admin)
//   - "compact": testo medio rosa chiaro (riservato a colonne secondarie)
//   - "compact-italic": come compact ma in italico e ~10% più piccolo,
//     usato per la sezione admin "strumenti operativi/manutenzione" che
//     l'utente vuole distinguere visivamente dalla top-list.
function MenuItem({ label, onClick, testid, variant = "default", disabled = false }) {
  let cls;
  if (variant === "compact-italic") {
    // 10% più piccoli del "compact": 0.81rem / 1.0125rem invece di 0.9 / 1.125.
    cls = "italic text-[0.81rem] sm:text-[1.0125rem] font-semibold text-[#f8dfd6] hover:text-white transition-colors text-left";
  } else if (variant === "compact") {
    cls = "text-[0.9rem] sm:text-[1.125rem] font-semibold text-[#f8dfd6] hover:text-white transition-colors text-left";
  } else {
    cls = "text-[1.05rem] sm:text-[1.6875rem] font-semibold text-white hover:text-[#f8dfd6] transition-colors text-left";
  }
  if (disabled) cls += " opacity-40 cursor-not-allowed";
  return (
    <button
      onClick={disabled ? undefined : onClick}
      data-testid={testid}
      disabled={disabled}
      className={cls}
    >
      {label}
    </button>
  );
}

// Corpo del menu fullscreen.
// - Utente normale: lista verticale unica con tutte le pagine consentite.
// - Admin: due colonne affiancate. La colonna di sinistra contiene gli
//   strumenti operativi quotidiani (Piano di Cura + Chiama Paziente).
//   La colonna di destra contiene gli strumenti gestionali/analitici nella
//   sequenza richiesta dall'utente.
function MenuBody({ isAdmin, user, can, canInstall, isMobileDevice, installLabel, onInstallClick, onNavigate }) {
  // Sorgente unica di verità: definisco ogni voce una sola volta.
  // Le condizioni `can(...)` filtrano l'accesso (admin bypassa tutto).
  // Permesso "Gestione listino": admin OR user con flag manage_treatments.
  const canManageTreatments = isAdmin || !!user?.manage_treatments;
  const canUseAiAssistant = isAdmin || !!user?.ai_assistant;
  const left = [
    can("chiama-paziente") && { key: "chiama-paziente", label: "Chiama Paziente", path: "/chiama-paziente" },
    can("piano-cura") && { key: "piano-cura-clinico", label: "Piano di Cura — Reparto Clinico", path: "/piano-cura-clinico" },
    can("piano-cura") && { key: "piano-cura-ortodonzia", label: "Piano di Cura — Ortodonzia", path: "/piano-cura-ortodonzia" },
    can("piani-cura") && { key: "piani-cura", label: "Pipeline", path: "/piani-cura" },
    can("piano-cura") && { key: "opportunita-create", label: "Archivio Piani di Cura", path: "/opportunita-create" },
    canUseAiAssistant && { key: "preventivi", label: "Archivio Preventivi (AI)", path: "/preventivi" },
    canUseAiAssistant && { key: "knowledge-base", label: "Knowledge Base Clinica", path: "/knowledge-base" },
    can("chiama-paziente") && { key: "promemoria-clinico", label: "Promemoria Reparto Clinico", path: "/promemoria-clinico" },
    can("chiama-paziente") && { key: "promemoria-ortodonzia", label: "Promemoria Ortodonzia", path: "/promemoria-ortodonzia" },
  ].filter(Boolean);

  // Ordine richiesto dall'utente per la colonna admin (destra).
  // Strutturato in 2 gruppi separati da una riga vuota:
  //  - top: voci principali in stile "compact" (testo medio)
  //  - bottom: voci secondarie in italico, ~10% più piccole
  // "Report Opportunità" è stato rimosso su richiesta esplicita dell'utente.
  // "Storico Stage" non era in lista ma viene tenuto come ultima voce
  // italica per non rompere il workflow attuale.
  const adminTop = [
    can("dashboard") && { key: "dashboard", label: "Dashboard", path: "/dashboard" },
    can("pazienti-ricorrenti") && { key: "pazienti-ricorrenti", label: "Pazienti Ricorrenti", path: "/pazienti-ricorrenti" },
    can("confronto-listini") && { key: "confronto-listini", label: "Confronto Listini", path: "/confronto-listini" },
    can("feedback") && { key: "feedback", label: "Feedback", path: "/feedback" },
    // "Pannello Assicurazioni" è il nuovo nome di Storico Stage.
    // Path invariato per non rompere bookmark/link esistenti.
    can("storico-stage") && { key: "storico-stage", label: "Pannello Assicurazioni", path: "/storico-stage" },
    can("storico-stage") && { key: "archivio-pratiche", label: "Archivio Pratiche", path: "/archivio-pratiche" },
    can("video-tutorial") && { key: "video-tutorial", label: "Video Tutorial", path: "/video-tutorial" },
  ].filter(Boolean);

  const adminBottom = [
    isAdmin && { key: "matching", label: "Matching Contatti", path: "/matching" },
    can("matching-appuntamenti") && { key: "matching-appuntamenti", label: "Multiappuntamenti", path: "/matching-appuntamenti" },
    isAdmin && { key: "appuntamenti-duplicati", label: "Calendario", path: "/appuntamenti-duplicati" },
    isAdmin && { key: "pulizia-calendario", label: "Pulizia Calendario", path: "/pulizia-calendario" },
    isAdmin && { key: "log-attivita", label: "Log Attività", path: "/log-attivita" },
    { key: "salvataggi-temporanei", label: "Salvataggi Temporanei", path: "/salvataggi-temporanei" },
    can("monitor") && { key: "monitor", label: "Monitor", path: "/monitor" },
    isAdmin && { key: "admin", label: "Gestione Utenti", path: "/admin" },
    isAdmin && { key: "istruzioni-ai", label: "Istruzioni AI", path: "/istruzioni-ai" },
    canManageTreatments && { key: "gestione-listino", label: "Gestione Listino", path: "/gestione-listino" },
    // Installa App: SOLO su smartphone (Android/iPhone). Esclude desktop
    // e tablet — l'app è pensata per essere installata sul telefono dello
    // staff per l'accesso veloce in studio (richiesta utente 26/06/2026).
    isMobileDevice && canInstall && { key: "install-app", label: installLabel, action: onInstallClick },
  ].filter(Boolean);

  // Per i non-admin manteniamo lo STESSO layout a 2 colonne dell'admin
  // (richiesta utente 28/05/2026). La colonna sinistra è identica
  // (`left`); a destra mostriamo le voci secondarie consentite — stesso
  // stile "compact"/"compact-italic" usato per l'admin.
  if (!isAdmin) {
    const userRightTop = [
      can("dashboard") && { key: "dashboard", label: "Dashboard", path: "/dashboard" },
      can("pazienti-ricorrenti") && { key: "pazienti-ricorrenti", label: "Pazienti Ricorrenti", path: "/pazienti-ricorrenti" },
      can("confronto-listini") && { key: "confronto-listini", label: "Confronto Listini", path: "/confronto-listini" },
      can("feedback") && { key: "feedback", label: "Feedback", path: "/feedback" },
      can("storico-stage") && { key: "storico-stage", label: "Pannello Assicurazioni", path: "/storico-stage" },
      can("storico-stage") && { key: "archivio-pratiche", label: "Archivio Pratiche", path: "/archivio-pratiche" },
      can("video-tutorial") && { key: "video-tutorial", label: "Video Tutorial", path: "/video-tutorial" },
    ].filter(Boolean);
    const userRightBottom = [
      can("matching-appuntamenti") && { key: "matching-appuntamenti", label: "Multiappuntamenti", path: "/matching-appuntamenti" },
      can("monitor") && { key: "monitor", label: "Monitor", path: "/monitor" },
      can("piano-cura") && { key: "salvataggi-temporanei", label: "Salvataggi Temporanei", path: "/salvataggi-temporanei" },
      canManageTreatments && { key: "gestione-listino", label: "Gestione Listino", path: "/gestione-listino" },
      isMobileDevice && canInstall && { key: "install-app", label: installLabel, action: onInstallClick },
    ].filter(Boolean);
    return (
      <div className="flex-1 flex items-center px-6 sm:px-20 font-mono-menu">
        <div className="w-full grid grid-cols-1 md:grid-cols-2 items-start gap-x-12 gap-y-2 sm:gap-y-4">
          <div className="flex flex-col gap-2 sm:gap-4">
            {left.map((it) => (
              <MenuItem
                key={it.key}
                label={it.label}
                testid={`menu-${it.key}`}
                onClick={() => onNavigate(it.path)}
              />
            ))}
          </div>
          <div className="flex flex-col gap-1.5 sm:gap-2.5">
            {userRightTop.map((it) => (
              <MenuItem
                key={it.key}
                label={it.label}
                testid={`menu-${it.key}`}
                variant="compact"
                onClick={() => (it.action ? it.action() : onNavigate(it.path))}
              />
            ))}
            {userRightBottom.length > 0 && (
              <div className="h-3 sm:h-5" aria-hidden="true" />
            )}
            {userRightBottom.map((it) => (
              <MenuItem
                key={it.key}
                label={it.label}
                testid={`menu-${it.key}`}
                variant="compact-italic"
                onClick={() => (it.action ? it.action() : onNavigate(it.path))}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Admin: layout a 2 colonne (su mobile cade a 1 colonna automatica).
  // Il wrapper flex centra verticalmente il blocco; il grid interno usa
  // `items-start` così le due colonne sono allineate in alto tra loro
  // (la sinistra ha meno voci della destra).
  return (
    <div className="flex-1 flex items-center px-6 sm:px-20 font-mono-menu">
      <div className="w-full grid grid-cols-1 md:grid-cols-2 items-start gap-x-12 gap-y-2 sm:gap-y-4">
        <div className="flex flex-col gap-2 sm:gap-4">
          {left.map((it) => (
            <MenuItem key={it.key} label={it.label} testid={`menu-${it.key}`} onClick={() => onNavigate(it.path)} />
          ))}
        </div>
        <div className="flex flex-col gap-1.5 sm:gap-2.5">
          {adminTop.map((it) => (
            <MenuItem
              key={it.key}
              label={it.label}
              testid={`menu-${it.key}`}
              variant="compact"
              disabled={it.disabled}
              onClick={() => (it.action ? it.action() : onNavigate(it.path))}
            />
          ))}
          {/* Spacer fra il gruppo top e quello italico (riga vuota richiesta). */}
          <div className="h-3 sm:h-5" aria-hidden="true" />
          {adminBottom.map((it) => (
            <MenuItem
              key={it.key}
              label={it.label}
              testid={`menu-${it.key}`}
              variant="compact-italic"
              onClick={() => (it.action ? it.action() : onNavigate(it.path))}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
